import { makeAutoObservable, reaction, runInAction, toJS } from 'mobx';

import { analytics, dataLayerActions } from '@/app/parts/Analytics';

import { getBetBuilderCoefficient, getBetBuilderOutcomes } from '@/lib/api/auth';
import { logger } from '@/lib/api/logger';
import { sendMsg } from '@/lib/api/postMessageAPI';
import { RootStoreMobx } from '@/lib/mobx/RootStoreMobx';
import { getHashFromString } from '@/helpers/getHashFromString';
import { isEmpty } from '@/helpers/isEmpty';
import { LS } from '@/helpers/localStorageHelper';
import { throttle } from '@/helpers/throttle';
import { betTrackMark } from '@/config/constants';

import { Bet } from './Bet.mobx';

class BetBuilder {
  root: RootStoreMobx;

  private static readonly COEFFICIENT_UPDATE_THROTTLE_MS = 1000;

  constructor(model: RootStoreMobx) {
    makeAutoObservable(this);

    this.root = model;

    reaction(
      () => [this.root.betslip.currentTab, toJS(this.betBuilderData)],
      ([currentTab, betBuilderData]) => {
        if (betBuilderData && typeof betBuilderData === 'object' && !isEmpty(betBuilderData)) {
          if (this.root.ui.activeTab !== 'betslip') {
            this.root.ui.setActiveTab('betslip');
          }
          if (currentTab !== 'SINGLEBET') {
            this.root.betslip.setCurrentTab('SINGLEBET');
          }
        }
      },
    );

    reaction(
      () => [this.root.store?.activeEvent, this.root.ui?.isEventPage],
      () => {
        this.betBuilderOutcomesIds = {};
      },
    );
  }

  betBuilderData: { [key: string]: OutcomeData[] } = {};

  betBuilderInProgress = false;

  betBuilderOutcomesIds: BetBuilderOutcomesIds = {};

  betBuilderMarketsListCache: { [eventId: string]: BetBuilderOutcomesIds } = {};

  betBuilderMarketsListPromises: { [eventId: string]: Promise<BetBuilderOutcomesIds> } = {};

  isBetBuilderActive = false;

  currentBetBuilderCoefficient: null | number = null;

  getCoefficient = async ({
    key = '',
    outcomeId = '',
    removedOutcomeId = '',
  }: GetCoefficientParams): Promise<CoefficientData> => {
    this.betBuilderInProgress = true;
    const fakeDataResponse: CoefficientData = { coefficient: 0, errorCode: null, maxbet: 0 };
    const currentData = this.betBuilderData[key] ?? [];
    try {
      const outcomeIds: string[] = currentData.reduce((acc, { outcomeId: id }) => {
        if (id && id !== removedOutcomeId) {
          acc.push(id);
          return acc;
        }
        return acc;
      }, [] as string[]);

      if (outcomeId) {
        outcomeIds.push(outcomeId);
      }

      const fakeData: CoefficientResponse = { data: fakeDataResponse };
      const { data } =
        outcomeIds.length <= 1
          ? fakeData
          : await getBetBuilderCoefficient({
              currency: this.root.profile.currency.name,
              outcomeIds,
            });

      if (key in this.betBuilderData) {
        this.betBuilderData[key] = currentData.filter(({ outcomeId: id }) =>
          outcomeIds.includes(id),
        );
      }

      return data?.data ?? fakeDataResponse;
    } catch (error) {
      logger.error('Error getBetBuilderCoefficient', { error: error as Error });
      return fakeDataResponse;
    } finally {
      this.betBuilderInProgress = false;
    }
  };

  setBetBuilderData = ({
    outcomeId = '',
    eventId = '',
    name = '',
    marketName = '',
    pidLid = '',
    coefficient,
    isShareBet = false,
  }: SetBetBuilderDataParams): void => {
    const key = getHashFromString(eventId.split('.').pop() || '').toString();
    const outcomesData: OutcomeData = { marketName, name, outcomeId, pidLid };
    if (key in this.betBuilderData) {
      const existingOutcome = (this.betBuilderData[key] ?? []).find(
        (o) => o.outcomeId === outcomeId,
      );

      if (!existingOutcome) {
        this.betBuilderData[key] = [...(this.betBuilderData[key] ?? []), outcomesData];
        this.setBetBuilderOutcomes({ key });
        this.setBetBuilderCoefficient({ coefficient, key });
      }
    } else {
      this.betBuilderData[key] = [outcomesData];
      this.addBetBuilder({ coefficient, eventId, id: key, isShareBet, marketName, name });
    }
  };

  removeBetBuilderItem = ({ eventId = '' }: RemoveBetBuilderItemParams): void => {
    const key = getHashFromString(eventId.split('.').pop() || '').toString();
    this.betBuilderData = Object.keys(this.betBuilderData).reduce<{ [key: string]: OutcomeData[] }>(
      (acc, betBuilderId = '') => {
        if (betBuilderId !== key) {
          acc[betBuilderId] = this.betBuilderData[betBuilderId] ?? [];
        }
        return acc;
      },
      {},
    );

    this.removeBetBuilder(key);
  };

  removeBetBuilderOutcomeId = async ({
    outcomeId = '',
    eventId = '',
    getCoefficient = undefined,
    isGetCoefficient = undefined,
  }: RemoveBetBuilderOutcomeIdParams): Promise<Boolean> => {
    const key = getHashFromString(eventId.split('.').pop() || '').toString();

    const shouldGetCoefficient = getCoefficient ?? isGetCoefficient ?? false;
    if (!this.betBuilderData[key]) {
      return false;
    }

    try {
      const betToUpdate = this.root.betslip.bets.find((bet) => bet.id === key);
      const removedOutcome = this.betBuilderData[key].find((item) => item.outcomeId === outcomeId);

      const outcomesData: OutcomeData[] =
        this.betBuilderData[key].filter((item) => item.outcomeId !== outcomeId) ?? [];

      if (!outcomesData.length) {
        this.removeBetBuilderItem({ eventId });
        return true;
      }

      if (betToUpdate && removedOutcome) {
        const betWithRemovedOutcome = {
          ...betToUpdate,
          betBuilderOutcomes: [removedOutcome],
        };

        analytics.pushBetInfo({
          action: dataLayerActions.REMOVE_BET,
          bets: [betWithRemovedOutcome],
        });
      }

      this.betBuilderData[key] = [...outcomesData];
      this.setBetBuilderOutcomes({ key });

      if (!shouldGetCoefficient) {
        return true;
      }

      const {
        coefficient = 0,
        errorCode = null,
        maxbet,
      }: CoefficientData = await this.getCoefficient({
        key,
      });

      if (!errorCode) {
        this.setBetBuilderCoefficient({ coefficient, key });
        this.setBetBuilderMaxBet({ key, maxbet });
        return true;
      }
      return false;
    } catch (error) {
      logger.error('Error removeBetBuilderOutcomeId', { error: error as Error });
      return false;
    }
  };

  addBetBuilder = ({
    id = '',
    eventId = '',
    coefficient,
    isShareBet = false,
  }: AddBetBuilderParams): void => {
    const key = getHashFromString(eventId.split('.').pop() || '').toString();
    const {
      name: eventName = '',
      slug = '',
      did = '',
    } = (this.root.store.events.get(eventId) ?? {}) as BetBuilderEvent;

    const shareBetTrackMark = isShareBet
      ? [betTrackMark.sharedBet, this.root.shareBet.shareBetCode]
      : [];
    const trackMarks = [betTrackMark.betBuilder, ...shareBetTrackMark];
    const newBet = new Bet(
      {
        betBuilderOutcomes: this.betBuilderData[key],
        coefficient,
        disciplineId: did,
        eventId,
        eventName,
        id,
        isBetBuilder: true,
        slug,
        trackMarks,
      },
      this.root.store,
      this.root.betslip,
    );

    runInAction(() => {
      this.root.betslip.bets.push(newBet);
      this.root.betslip.addToStorage(newBet);

      newBet.betBuilderOutcomes.map(({ outcomeId }) => {
        sendMsg('addBet', { id: outcomeId, isBetBuilder: true });
      });

      analytics.pushBetInfo({
        action: dataLayerActions.ADD_BET,
        bets: [newBet],
      });
    });
  };

  removeBetBuilder = (id: string): void => {
    const betToRemove = this.root.betslip.bets.find((bet) => bet.id === id);
    const filteredBets = this.root.betslip.bets.filter((bet) => bet.id !== id);
    this.root.betslip.bets = [...filteredBets];
    this.root.betslip.removeFromStorage(id);

    sendMsg('removeBet', { eventId: betToRemove?.eventId, isBetBuilder: true });

    if (betToRemove) {
      analytics.pushBetInfo({
        action: dataLayerActions.REMOVE_BET,
        bets: [betToRemove],
      });
    }
  };

  restoreShareBetBuilder = async (bet: {
    betBuilderData: {
      betBuilderOutcomes?: OutcomeData[];
      eventId?: string;
      isBetBuilderSupported: boolean;
      isLast?: boolean;
      marketName?: string;
      outcomeName?: string;
      marketLid?: string;
      outcomeId?: string;
      sessionId?: string;
    };
    isShareBet?: boolean;
  }): Promise<void> => {
    const {
      eventId = '',
      marketName = '',
      outcomeName = '',
      outcomeId = '',
      marketLid = '',
      isLast = false,
      isBetBuilderSupported = false,
    } = bet.betBuilderData;
    const event = this.root.store.events.get(eventId);
    if (!event) {
      this.root.ui.setAlert({
        text: 'BETBUILDER.CLOSE.ERROR',
        type: 'error',
      });
      return;
    }

    const restoreBet = async () => {
      this.setBetBuilderData({
        coefficient: 0,
        eventId,
        isShareBet: bet.isShareBet ?? false,
        marketName,
        name: outcomeName,
        outcomeId,
        pidLid: marketLid,
      });
      const key = getHashFromString(eventId.split('.').pop() || '').toString();
      const betBuilderItem = this.betBuilderData[key];
      const currentLength = betBuilderItem?.length || 0;

      if (isLast && betBuilderItem && currentLength > 0) {
        if (currentLength === 1) {
          runInAction(async () => {
            this.setBetBuilderCoefficient({ coefficient: 0, key });
          });
        } else {
          await this.updateCoefficient(eventId);
        }
      }
    };

    if ('isBetBuilderSupported' in event && event.isBetBuilderSupported) {
      try {
        const marketsList = await this.getBetBuilderMarketsList(eventId.split('.').pop() ?? '');
        const isPureMarket = (marketsList[marketLid.split('.').pop() ?? ''] ?? []).includes(
          outcomeId,
        );
        if (isPureMarket) {
          await restoreBet();
        }
      } catch (error) {
        logger.error('Error restoreBetBuilder', { error: error as Error });
      }

      return;
    }

    if (isBetBuilderSupported) {
      await restoreBet();
    }
  };

  restoreBetBuilder = async (bet: {
    betBuilderOutcomes?: OutcomeData[];
    eventId?: string;
  }): Promise<void> => {
    const restoredBet = new Bet({ ...bet }, this.root.store, this.root.betslip);
    const { betBuilderOutcomes = [], eventId = '' } = restoredBet as ResponseBet;
    const key = getHashFromString(eventId.split('.').pop() || '').toString();
    const outcomesData = this.betBuilderData[key] ?? [];
    this.betBuilderData[key] = [...outcomesData, ...betBuilderOutcomes];
    this.root.betslip.bets.push(restoredBet);
    try {
      const { coefficient, errorCode, maxbet }: CoefficientData = await this.getCoefficient({
        key,
      });
      if (!errorCode) {
        this.setBetBuilderCoefficient({ coefficient, key });
        this.setBetBuilderMaxBet({ key, maxbet });
      }
    } catch (error) {
      logger.error('Error restoreBetBuilder', { error: error as Error });
    }
  };

  setBetBuilderOutcomes = ({ key = '' }: SetBetBuilderOutcomesParams): void => {
    this.root.betslip.bets.forEach((bet) => {
      if (bet.id === key) {
        bet.betBuilderOutcomes = this.betBuilderData[key];
        const bets = (
          JSON.parse(LS.get('esSingleBets') || '[]') as {
            id: string;
            betBuilderOutcomes: OutcomeData[];
          }[]
        ).map((el) => {
          if (el.id === key) {
            el.betBuilderOutcomes = bet.betBuilderOutcomes;
          }
          return el;
        });
        LS.set('esSingleBets', JSON.stringify(bets));

        if (bet.betBuilderOutcomes && bet.betBuilderOutcomes.length > 1) {
          analytics.pushBetInfo({
            action: dataLayerActions.ADD_BET,
            bets: [bet],
          });
        }
      }
    });
  };

  setBetBuilderCoefficient = ({ key = '', coefficient }: SetBetBuilderCoefficientParams): void => {
    for (const bet of this.root.betslip.bets) {
      if (bet.id !== key) continue;

      bet.updateProp('coefficient', coefficient);

      return;
    }
  };

  setBetBuilderMaxBet = ({ key = '', maxbet }: SetBetBuilderMaxBetParams): void => {
    for (const bet of this.root.betslip.bets) {
      if (bet.id !== key) continue;

      bet.updateProp('maxBet', maxbet);

      return;
    }
  };

  getBetBuilderMarketsList = async (eventId: string): Promise<BetBuilderOutcomesIds> => {
    const normalizedId = eventId.split('.').pop() ?? eventId;
    if (this.betBuilderMarketsListCache[normalizedId]) {
      return this.betBuilderMarketsListCache[normalizedId];
    }

    if (this.betBuilderMarketsListPromises[normalizedId]) {
      return this.betBuilderMarketsListPromises[normalizedId];
    }

    const promise = this.fetchBetBuilderMarketsList(normalizedId);
    this.betBuilderMarketsListPromises[normalizedId] = promise;

    try {
      const result = await promise;
      this.betBuilderMarketsListCache[normalizedId] = result;
      return result;
    } catch (error) {
      return {};
    } finally {
      delete this.betBuilderMarketsListPromises[normalizedId];
    }
  };

  private fetchBetBuilderMarketsList = async (eventId: string): Promise<BetBuilderOutcomesIds> => {
    try {
      const {
        data: { data = [] },
      } = await getBetBuilderOutcomes(eventId.split('.').pop() ?? eventId);

      return data.reduce((acc: BetBuilderOutcomesIds, { id = '', outcomes = [] }) => {
        const outcomesList = outcomes.map(({ id: outcomeId = '' }) => outcomeId);
        if (id in acc) {
          acc[id] = [...(acc[id] ?? []), ...outcomesList];
        } else {
          acc[id] = [...outcomesList];
        }
        return acc;
      }, {} as BetBuilderOutcomesIds);
    } catch (error) {
      logger.error('getBetBuilderMarketsList failed', {
        error: error as Error,
      });
      return {};
    }
  };

  getBetBuilderByEvent = async (eventId: string): Promise<void> => {
    this.betBuilderOutcomesIds = {};

    if (this.root.store?.activeEvent !== eventId) {
      return;
    }

    try {
      this.betBuilderOutcomesIds = await this.getBetBuilderMarketsList(eventId);
    } catch (error) {
      logger.error('getBetBuilderByEvent failed', {
        error: error as Error,
      });
    }
  };

  setIsBetBuilderActive = (bool = false) => {
    this.isBetBuilderActive = bool;
  };

  setCurrentBetBuilderCoefficient = (coefficient: number) => {
    this.currentBetBuilderCoefficient = coefficient;
  };

  updateCoefficient = async (eventId = '') => {
    const key = getHashFromString(eventId.split('.').pop() || '').toString();
    try {
      const { coefficient, errorCode, maxbet } = (await this.getCoefficient({
        key,
      })) as CoefficientData;
      if (!errorCode) {
        this.setBetBuilderCoefficient({ coefficient, key });
        this.setBetBuilderMaxBet({ key, maxbet });
      } else if (errorCode === 506) {
        await this.handleError506(eventId, key);
      }
    } catch (error) {
      logger.error('Error updateBetBuilderCoefficient', { error: error as Error });
    }
  };

  handleError506 = async (eventId: string, key: string) => {
    const betBuilderItem = this.betBuilderData[key];
    if (!betBuilderItem || betBuilderItem.length === 0) {
      return;
    }

    if (betBuilderItem.length === 1) {
      this.root.ui.setAlert({
        text: 'SHAREBET.ERROR.SOME_MARKETS',
        type: 'error',
      });
      this.setBetBuilderCoefficient({ coefficient: 0, key });
      return;
    }

    const updatedItem = [...betBuilderItem];
    updatedItem.pop();
    this.betBuilderData[key] = updatedItem;
    await this.updateCoefficient(eventId);
  };

  updateBetBuilderCoefficient = throttle((eventId: string) => {
    this.updateCoefficient(eventId);
  }, BetBuilder.COEFFICIENT_UPDATE_THROTTLE_MS);
}

export { BetBuilder };

interface OutcomeData {
  outcomeId: string;
  name: string;
  marketName: string;
  pidLid: string;
}

interface GetCoefficientParams {
  key: string;
  outcomeId?: string;
  removedOutcomeId?: string;
}

interface SetBetBuilderDataParams {
  outcomeId: string;
  eventId: string;
  name: string;
  marketName: string;
  pidLid: string;
  coefficient: number;
  isShareBet?: boolean;
}

interface RemoveBetBuilderItemParams {
  eventId: string;
}

interface RemoveBetBuilderOutcomeIdParams {
  readonly outcomeId: string;
  readonly eventId: string;
  getCoefficient?: boolean;
  isGetCoefficient?: boolean;
}

interface AddBetBuilderParams {
  id: string;
  eventId: string;
  name: string;
  marketName: string;
  coefficient: number;
  isShareBet: boolean;
}

interface SetBetBuilderOutcomesParams {
  key: string;
}

interface SetBetBuilderCoefficientParams {
  key: string;
  coefficient: number;
}

interface SetBetBuilderMaxBetParams {
  key: string;
  maxbet: number;
}

interface CoefficientData {
  coefficient: number;
  errorCode: number | null;
  maxbet: number;
}

interface CoefficientResponse {
  data: CoefficientData;
}

interface BetBuilderEvent {
  did?: string;
  eventId: string;
  id: string;
  marketName: string;
  name: string;
  slug?: string;
}

interface ResponseBet {
  betBuilderOutcomes?: OutcomeData[];
  eventId?: string;
}

type BetBuilderOutcomesIds = Record<string, string[]>;
